import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { map } from 'rxjs/operators';

import { HypecastOAuthConfig } from '@app/shared/authentication/models/hypecast-oauth-config';
import {
  HypecastAuthenticationService,
  HypecastCredentials,
} from '@app/shared/authentication/services/authentication.service';
import { HypecastOAuthService } from '@app/shared/authentication/services/hypecast-oauth.service';
import { HYPECAST_OAUTH_CONFIG } from '@app/shared/authentication/tokens/hypecast-oauth-config.token';
import { ImageComponent } from '@app/shared/components/image/image.component';
import { ApplicationUser } from '@app/shared/models/classes/ApplicationUser';
import { Tenant } from '@app/shared/models/classes/Tenant';
import { RoleHelper } from '@app/shared/models/local/role.helper';
import { ITenantService } from '@app/shared/services/itenant.service';

/**
 * The tenant image of the sidebar: the square company icon when collapsed (the wide logo
 * would be squeezed into the 36 px box), otherwise the logo; null without either.
 */
export function sidebarTenantImage(
  tenant: Pick<Tenant, 'logoUrl' | 'iconUrl'> | null | undefined,
  collapsed: boolean,
): { src: string; icon: boolean } | null {
  const icon = tenant?.iconUrl?.trim();
  if (collapsed && icon) {
    return { src: icon, icon: true };
  }
  const logo = tenant?.logoUrl?.trim();
  return logo ? { src: logo, icon: false } : null;
}

@Component({
  selector: 'hypecast-admin-user-info',
  templateUrl: './user-info.component.html',
  styleUrl: './user-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterLink, MatIconModule, MatMenuModule, ImageComponent],
})
export class UserInfoComponent {
  private readonly oAuthConfig = inject<HypecastOAuthConfig>(HYPECAST_OAUTH_CONFIG);
  private readonly hypecastOAuthService = inject(HypecastOAuthService);
  private readonly tenantService = inject(ITenantService);
  private readonly authenticationService = inject(HypecastAuthenticationService);

  readonly isCollapsed = input(false);
  readonly showTenantOnly = input(false);

  readonly user = toSignal<ApplicationUser | null>(
    this.authenticationService.credentials$.pipe(map((credentials: HypecastCredentials) => credentials.user)),
    { initialValue: null },
  );
  readonly tenant = this.tenantService.tenantSignal;

  readonly sidebarImage = computed(() => sidebarTenantImage(this.tenant(), this.isCollapsed()));

  readonly RoleHelper = RoleHelper;

  logout(): void {
    this.tenantService.resetTenant();

    // Deliberate logout: re-enable redirects so logOut() can reach the end-session endpoint.
    this.oAuthConfig.allowLoginRedirects();
    this.hypecastOAuthService.logOut();
    this.authenticationService.logout();
  }
}
